import React from 'react';
import { getPointScoreEmoji, getPointScoreColor, getPointScoreTextColor } from '../../utils/scoreConfig';

// Circular Progress Ring Component
function CircularProgress({ score, maxScore = 10, size = 140, pending = false }) {
  const safeMaxScore = Math.max(1, Number(maxScore) || 1);
  const percentage = pending ? 0 : Math.min((score / safeMaxScore) * 100, 100);
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
        style={{ width: size, height: size }}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          className="text-slate-100 dark:text-slate-600"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="text-green-600 dark:text-green-500 transition-all duration-1000 ease-out"
          style={{
            strokeDasharray,
            strokeDashoffset,
          }}
        />
      </svg>
      {/* Score text */}
      <div className="absolute inset-0 flex items-center justify-center">
        {pending ? (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-slate-400 dark:text-slate-500">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
          </svg>
        ) : (
          <span className="text-2xl font-bold text-slate-800 dark:text-slate-200">
            {score}/{maxScore}
          </span>
        )}
      </div>
    </div>
  );
}


// Main Eligibility Card Component
export function EligibilityCard({
  assessmentTitle = "Assessment Einzelhandelsfachkraft",
  templateName = null,
  submittedAt,
  duration,
  totalScore = null,
  totalMaxScore = null,
  status = null,
  pageBreakdown = [],
  bonusControl = null
}) {
  // Format duration from seconds to MM:SS format
  const formatDuration = (duration) => {
    if (!duration) return "0:00";
    
    // If duration is already in MM:SS format (string with colon), return as-is
    if (typeof duration === 'string' && duration.includes(':')) {
      return duration;
    }
    
    // If duration is in seconds (number), convert to MM:SS
    const numDuration = parseInt(duration);
    if (isNaN(numDuration)) return "0:00";
    
    const minutes = Math.floor(numDuration / 60);
    const seconds = numDuration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Unbekannt";
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 p-6 mb-6">
      <div className="flex flex-col gap-8">
        {/* Meta information across the full width */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Antworten auf: <strong className="text-slate-800 dark:text-slate-200">{assessmentTitle}</strong></span>
          </div>
          {templateName && (
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7l8-4 8 4-8 4-8-4z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12l8 4 8-4M4 17l8 4 8-4" />
              </svg>
              <span>Vorlage: <span className="text-slate-700 dark:text-slate-300">{templateName}</span></span>
            </div>
          )}
          <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Eingereicht: {formatDate(submittedAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>Dauer: {formatDuration(duration)}</span>
            </div>
          </div>
        </div>

        {/* Status, and no reserved space when there is none */}
        <div className="flex justify-center empty:hidden">
          {status === 'Bewerbung zurückgezogen' && (
            <div className="flex items-center gap-3 px-4 py-3 bg-orange-100 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700 rounded-xl">
              <div className="w-8 h-8 bg-orange-200 dark:bg-orange-800 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold text-orange-800 dark:text-orange-200">
                  Bewerbung zurückgezogen
                </div>
                <div className="text-xs text-orange-600 dark:text-orange-400">
                  Diese Bewerbung wurde vom Bewerber abgesagt
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Score, centred: it is what the card is about */}
        <div className="flex flex-col items-center space-y-4">
          <div className="flex items-center gap-6 flex-wrap justify-center">
            {/* Total points — the single score, summed from the answers */}
            <div className="flex flex-col items-center space-y-2">
              <CircularProgress score={totalScore || 0} maxScore={totalMaxScore || 1} size={120} pending={totalScore === null || totalMaxScore === null} />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Gesamtscore</span>
              {totalScore !== null && totalMaxScore !== null && totalMaxScore > 0 ? (
                <div
                  className="text-2xl cursor-help"
                  title="Gesamte Punktebewertung"
                >
                  {getPointScoreEmoji(totalScore, totalMaxScore)}
                </div>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">
                  Noch nicht bewertet
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Per-page score breakdown — page subtotals sum to the Gesamtscore */}
      {Array.isArray(pageBreakdown) && pageBreakdown.length > 0 && (
        <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
          <div className="flex flex-wrap gap-2 justify-center">
            {pageBreakdown.map((page) => (
              <div key={page.key} className="flex items-center gap-2 px-3 py-1 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg">
                <span className="text-sm text-slate-600 dark:text-slate-300">{page.title}</span>
                <span className={`font-mono text-sm font-bold ${getPointScoreColor(page.pageScore, page.pageMax)} ${getPointScoreTextColor(page.pageScore, page.pageMax)} px-2 py-0.5 rounded`}>
                  {page.pageScore}/{page.pageMax}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bonus / Malus — manual adjustment to the total */}
      {bonusControl && (
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
          {bonusControl}
        </div>
      )}
    </div>
  );
}
